import React, { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../ui/command'
import {
  LayoutDashboard,
  Building2,
  ShoppingBag,
  BarChart3,
  Settings,
  CheckSquare,
  CalendarDays,
  FileText,
  Target,
  Briefcase,
  PlusCircle,
} from 'lucide-react'

interface CommandMenuProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface CommandRoute {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  group: string
}

const routes: CommandRoute[] = [
  // Pages
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, group: 'Pages' },
  { name: 'Customers', href: '/customers', icon: Building2, group: 'Pages' },
  { name: 'Marketplace', href: '/marketplace', icon: ShoppingBag, group: 'Pages' },
  { name: 'Reports', href: '/reports', icon: BarChart3, group: 'Pages' },
  { name: 'Settings', href: '/settings', icon: Settings, group: 'Pages' },
  { name: 'Tasks', href: '/tasks', icon: CheckSquare, group: 'Pages' },
  { name: 'Contracts', href: '/contracts', icon: FileText, group: 'Pages' },
  { name: 'Calendar', href: '/calendar', icon: CalendarDays, group: 'Pages' },
  { name: 'Proposals', href: '/proposals', icon: FileText, group: 'Pages' },
  { name: 'Leads', href: '/leads', icon: Target, group: 'Pages' },
  { name: 'Projects', href: '/projects', icon: Briefcase, group: 'Pages' },
  // Quick Actions
  { name: 'Create Proposal', href: '/proposals/create', icon: PlusCircle, group: 'Quick Actions' },
  { name: 'Add Lead', href: '/leads/create', icon: PlusCircle, group: 'Quick Actions' },
  { name: 'Create Project', href: '/projects/create', icon: PlusCircle, group: 'Quick Actions' },
]

export default function CommandMenu({ open, onOpenChange }: CommandMenuProps) {
  const navigate = useNavigate()

  const handleSelect = useCallback(
    (href: string) => {
      onOpenChange(false)
      navigate(href)
    },
    [navigate, onOpenChange]
  )

  // Keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        onOpenChange(!open)
      }
    }

    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [open, onOpenChange])

  // Group routes
  const groups = routes.reduce((acc, route) => {
    if (!acc[route.group]) acc[route.group] = []
    acc[route.group].push(route)
    return acc
  }, {} as Record<string, CommandRoute[]>)

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {Object.entries(groups).map(([group, items], idx) => (
          <React.Fragment key={group}>
            {idx > 0 && <CommandSeparator />}
            <CommandGroup heading={group}>
              {items.map((item) => {
                const Icon = item.icon
                return (
                  <CommandItem
                    key={item.href}
                    value={item.name}
                    onSelect={() => handleSelect(item.href)}
                    className="cursor-pointer"
                  >
                    <Icon className="mr-2 h-4 w-4 text-slate-500" />
                    <span>{item.name}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </React.Fragment>
        ))}
      </CommandList>
    </CommandDialog>
  )
}
